import { useMemo, useState } from 'react';

import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import { toast } from 'sonner';
import AppDateField from '@/components/AppDateField';
import {
  AppBottomSheet,
  AppBottomSheetHeader,
  AppButton,
  AppButtonRow,
  AppCardBody,
  AppFieldGroup,
  AppInput,
  AppText,
  AppTextArea,
} from '../../../ui-foundation';
import {
  buildPetFoodDraft,
  buildPetFoodPayload,
  type SharedPetFoodRowInput,
} from '../../../ui-foundation/features/pets';

type PetFoodFormValue = SharedPetFoodRowInput & {
  id?: string | null;
};

type Props = {
  petId: string;
  petName: string;
  existingFood?: PetFoodFormValue | null;
  onClose: () => void;
  onSaved: () => void;
};

export default function PetFoodForm({ petId, petName, existingFood, onClose, onSaved }: Props) {
  const { user } = useApp();
  const draft = useMemo(() => buildPetFoodDraft(existingFood), [existingFood]);
  const [brandName, setBrandName] = useState(draft.brandName);
  const [flavour, setFlavour] = useState(draft.flavour);
  const [packageSize, setPackageSize] = useState(draft.packageSize);
  const [price, setPrice] = useState(draft.price);
  const [lastPurchaseDate, setLastPurchaseDate] = useState(draft.lastPurchaseDate);
  const [lastsForDays, setLastsForDays] = useState(draft.lastsForDays);
  const [refillReminderDaysBefore, setRefillReminderDaysBefore] = useState(draft.refillReminderDaysBefore);
  const [notes, setNotes] = useState(draft.notes);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const canSave = Boolean(user && brandName.trim() && lastPurchaseDate && lastsForDays.trim());

  const handleSave = async () => {
    if (!user || !canSave) return;
    setSaving(true);

    const payload = buildPetFoodPayload({
      userId: user.id,
      petId,
      draft: {
        brandName,
        flavour,
        packageSize,
        price,
        lastPurchaseDate,
        lastsForDays,
        refillReminderDaysBefore,
        notes,
      },
    });

    const { error } = existingFood?.id
      ? await (supabase as any).from('pet_food_profiles').update(payload).eq('id', existingFood.id)
      : await (supabase as any).from('pet_food_profiles').insert(payload);

    if (error) {
      console.error(error);
      toast.error('Could not save that pet food profile right now.');
      setSaving(false);
      return;
    }

    setSaving(false);
    onSaved();
  };

  const handleDelete = async () => {
    if (!existingFood?.id) return;
    setDeleting(true);
    const { error } = await (supabase as any).from('pet_food_profiles').delete().eq('id', existingFood.id);
    if (error) {
      console.error(error);
      toast.error('Could not remove that pet food profile right now.');
      setDeleting(false);
      return;
    }

    setDeleting(false);
    onSaved();
  };

  return (
    <AppBottomSheet onClose={onClose}>
      <AppBottomSheetHeader
        title={existingFood?.id ? `Edit food for ${petName}` : `Add food for ${petName}`}
        subtitle="Keep the current food, last purchase, and refill timing in one calm place."
      />

      <AppCardBody className="flex-1 overflow-y-auto px-5 py-5" gap="$lg">
        <AppFieldGroup label="Brand name">
          <AppInput
            tag="input"
            value={brandName}
            onChange={(event) => setBrandName((event.target as HTMLInputElement).value)}
            placeholder="e.g. Royal Canin"
          />
        </AppFieldGroup>

        <div className="grid gap-4 sm:grid-cols-2">
          <AppFieldGroup label="Flavour (optional)">
            <AppInput
              tag="input"
              value={flavour}
              onChange={(event) => setFlavour((event.target as HTMLInputElement).value)}
              placeholder="e.g. Chicken"
            />
          </AppFieldGroup>
          <AppFieldGroup label="Pack size (optional)">
            <AppInput
              tag="input"
              value={packageSize}
              onChange={(event) => setPackageSize((event.target as HTMLInputElement).value)}
              placeholder="e.g. 6kg or 24 tins"
            />
          </AppFieldGroup>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <AppFieldGroup label="Price (optional)">
            <AppInput
              tag="input"
              inputMode="decimal"
              value={price}
              onChange={(event) => setPrice((event.target as HTMLInputElement).value)}
              placeholder="e.g. 37.50"
            />
          </AppFieldGroup>
          <AppDateField
            label="Last purchase date"
            value={lastPurchaseDate}
            onChange={setLastPurchaseDate}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <AppFieldGroup label="Usually lasts for (days)">
            <AppInput
              tag="input"
              inputMode="numeric"
              value={lastsForDays}
              onChange={(event) => setLastsForDays((event.target as HTMLInputElement).value)}
              placeholder="e.g. 24"
            />
          </AppFieldGroup>
          <AppFieldGroup label="Flag refill this many days early">
            <AppInput
              tag="input"
              inputMode="numeric"
              value={refillReminderDaysBefore}
              onChange={(event) => setRefillReminderDaysBefore((event.target as HTMLInputElement).value)}
              placeholder="e.g. 3"
            />
          </AppFieldGroup>
        </div>

        <AppText fontSize="$xs" color="$colorMuted">
          Adulting uses the last purchase date plus your estimate to work out the likely runout date and when the refill window should start.
        </AppText>

        <AppFieldGroup label="Notes (optional)">
          <AppTextArea
            value={notes}
            onChange={(event) => setNotes((event.target as HTMLTextAreaElement).value)}
            rows={3}
            placeholder="Shop, subscription detail, or anything else worth keeping handy"
          />
        </AppFieldGroup>

        <AppButtonRow justifyContent="space-between">
          {existingFood?.id ? (
            <AppButton tone="destructive" onPress={handleDelete} disabled={deleting || saving}>
              {deleting ? 'Removing…' : 'Remove food profile'}
            </AppButton>
          ) : (
            <span />
          )}
          <AppButtonRow justifyContent="flex-end">
            <AppButton tone="outline" onPress={onClose}>
              Cancel
            </AppButton>
            <AppButton onPress={handleSave} disabled={!canSave || saving || deleting}>
              {saving ? 'Saving…' : existingFood?.id ? 'Save changes' : 'Save food profile'}
            </AppButton>
          </AppButtonRow>
        </AppButtonRow>
      </AppCardBody>
    </AppBottomSheet>
  );
}
